// 대장간 강화 순간 연출(c017, Ev.GearEnhance의 VFX 훅): 강화 카드(gear-enhance-card) 위에만 겹치는 작은 2D 캔버스.
// 성공 = 망치 타격 섬광 + 솟는 불꽃 + 떠오르는 불씨 + 금테 빛, 큰 성공(+10·+13·+15) = 충격 고리 둘·빛 띠·불꽃 두 배(단계별 색),
// 실패 = 섬광 없이 회청 균열·떨어지는 불씨(하락은 붉은 두 갈래), 보호 = 균열이 청색 방패 호에 막힌다. 박자·입자는 forgeFxModel.ts(순수).
// 마을 대장간 창 안에서만 보인다(던전 플레이 화면 위에는 그리지 않는다). 망치 섬광은 카드 안 국소 섬광이지만 FLASH_SAFETY 리미터(1초 3회·간격 334ms)를 거치고,
// 섬광 약화 설정이면 40%로, 움직임 줄이기면 입자·고리·빛 띠 없이 테 빛과 정지한 균열만 보인다. 캔버스·스프라이트·문자열은 처음 한 번 만들고 재사용한다.
import { Ev, type GameEvent } from '../shared/events';
import { Scene, type GameView } from '../shared/types';
import { reducedMotion } from './skillbar';
import { ForgeGate, ForgeSim, PRISM_COLORS, PartKind, forgeBeat, isTranscendEvent, type ForgeBeat } from './forgeFxModel';

/** 카드 밖 여백(px, 카드 배율 1 기준): 위로 솟는 불꽃이 카드 위로 조금 나간다 */
const PAD_X = 36, PAD_TOP = 96, PAD_BOTTOM = 36;
/** 카드 기준 폭(px): 화면 배율이 달라도 불꽃 크기가 카드에 맞게 */
const NOMINAL_W = 320;
/** 결과 이벤트 뒤 카드를 찾는 최대 시간(ms). 창이 닫혀 있으면 버린다 */
const FIND_MS = 400;

const hex = (c: number): string => '#' + (c & 0xffffff).toString(16).padStart(6, '0');
/** 불꽃 나이별 색(흰 → 금 → 주황) */
const SPARK_COL = ['#fffbe8', '#ffd76a', '#ff8a2a'] as const;
const EMBER_COL = '#ff6a2a';
const CRACK_CORE = '#05070e';
const CRACK_HOT = '#fff1e0';

export class ForgeFx {
  /** 시계(ms). 갤러리 결정적 캡처가 바꾼다 */
  static clock: () => number = () => performance.now();
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  readonly sim = new ForgeSim();
  private readonly gate = new ForgeGate();
  private cursor: number;
  private last = -1;
  private pendAt = -1;
  private pendOutcome = 0;
  private pendTo = 0;
  private pendFrom = 0;
  /** 초월이면 그 부위(대장간 초월 카드가 이 부위 카드), 아니면 '' */
  private pendSlot = '';
  private pillars = new Map<string, HTMLCanvasElement>();
  private seed = 0x2f6e2b1;
  private cssW = 0; private cssH = 0; private dpr = 1;
  private shown = false;
  private posX = 1e9; private posY = 1e9;
  private colorStr = '#ffffff'; private rimStr = '#ffffff';
  private readonly glow = new Map<string, HTMLCanvasElement>();
  private sheenSprite: HTMLCanvasElement | null = null;
  /** QA: 시작한 연출 수·섬광 승인/거부 */
  readonly stats = { started: 0, dropped: 0, flashes: 0, flashBlocked: 0 };
  /** QA: 테 빛 승인·옅게 줄인 수 */
  get rims(): { rims: number; rimDimmed: number } { return this.gate.stats; }
  private readonly onEvent = (e: GameEvent): void => {
    if (e.type !== Ev.GearEnhance) return;
    this.pendAt = ForgeFx.clock(); this.pendOutcome = e.a; this.pendTo = e.b; this.pendFrom = e.c;
    this.pendSlot = isTranscendEvent(e.c, e.b) ? (this.view.mastery?.last.slot ?? '') : '';
  };

  constructor(parent: HTMLElement, private readonly view: GameView) {
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;pointer-events:none;z-index:41;display:none;';
    this.canvas.dataset.testid = 'forge-fx';
    this.canvas.setAttribute('aria-hidden', 'true');
    parent.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    this.cursor = view.events.written;
  }

  /** 강화 카드(대장간 「강화」 탭). 창이 다시 그려지면 노드가 바뀌므로 연출 중에는 매 프레임 찾는다 */
  private card(): HTMLElement | null {
    if (this.slot) { const t = document.querySelector<HTMLElement>('[data-testid="transcend-card-' + this.slot + '"]'); if (t) return t; }
    return document.querySelector<HTMLElement>('[data-testid="gear-enhance-card"]');
  }
  /** 지금 도는 연출의 초월 부위('' = 일반 강화) */
  private slot = '';

  update(): void {
    const now = ForgeFx.clock();
    const dt = this.last < 0 ? 0 : Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    this.cursor = this.view.events.readFrom(this.cursor, this.onEvent);
    const village = this.view.flow.scene === Scene.Village;
    let started = false;
    if (this.pendAt >= 0) {
      this.slot = this.pendSlot;
      const card = village ? this.card() : null;
      if (card) { this.begin(card, forgeBeat(this.pendOutcome, this.pendTo, this.pendFrom), now); this.pendAt = -1; started = true; }
      else if (now - this.pendAt > FIND_MS) { this.pendAt = -1; this.stats.dropped++; }
    }
    if (!this.sim.active || !village) { this.hide(); return; }
    // 시작한 프레임은 진행하지 않는다(타격 순간 t = 0을 한 번 그린다)
    if (!started) this.sim.step(dt);
    const card = this.card();
    if (!card || !this.sim.active) { this.hide(); return; }
    this.draw(card);
  }

  private begin(card: HTMLElement, beat: ForgeBeat, now: number): void {
    const r = card.getBoundingClientRect();
    const k = this.scaleOf(r.width);
    const reduced = reducedMotion();
    // 망치 섬광·테 빛은 FLASH_SAFETY 리미터와 섬광 약화 설정을 거친다(ForgeGate)
    this.gate.begin(now, beat, this.view.settings.reduceFlash, reduced);
    this.stats.flashes = this.gate.stats.flashes; this.stats.flashBlocked = this.gate.stats.flashBlocked;
    this.seed = (Math.imul(this.seed, 1103515245) + 12345) >>> 0;
    this.sim.start(beat, r.width / k, r.height / k, this.seed, reduced, this.gate.flashOn, this.gate.rimScale);
    this.colorStr = hex(beat.color);
    this.rimStr = hex(beat.rim);
    this.stats.started++;
  }

  private scaleOf(w: number): number { return Math.max(0.6, Math.min(1.6, w / NOMINAL_W)); }

  private hide(): void {
    if (!this.shown) return;
    this.shown = false;
    this.canvas.style.display = 'none';
  }

  /** 색별 둥근 빛 스프라이트(처음 한 번 만든다) */
  private glowOf(color: string): HTMLCanvasElement {
    let c = this.glow.get(color);
    if (c) return c;
    c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    if (g) {
      const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.18, color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 64, 64);
    }
    this.glow.set(color, c);
    return c;
  }

  /** 빛기둥 스프라이트(가로 그라데이션: 투명 → 색 → 흰 → 색 → 투명, 색별로 처음 한 번) */
  private pillar(color: string): HTMLCanvasElement {
    let c = this.pillars.get(color);
    if (c) return c;
    c = document.createElement('canvas');
    c.width = 64; c.height = 128;
    const g = c.getContext('2d');
    if (g) {
      const grad = g.createLinearGradient(0, 0, 64, 0);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(0.3, color);
      grad.addColorStop(0.5, '#ffffff');
      grad.addColorStop(0.7, color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 64, 128);
      // 위아래로 부드럽게 사라지게(기둥 끝이 칼같이 잘리지 않게)
      g.globalCompositeOperation = 'destination-in';
      const v = g.createLinearGradient(0, 0, 0, 128);
      v.addColorStop(0, 'rgba(0,0,0,0)');
      v.addColorStop(0.3, 'rgba(0,0,0,1)');
      v.addColorStop(0.75, 'rgba(0,0,0,1)');
      v.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = v;
      g.fillRect(0, 0, 64, 128);
    }
    this.pillars.set(color, c);
    return c;
  }

  /** 카드를 가로지르는 빛 띠 스프라이트(가로 그라데이션, 처음 한 번) */
  private sheen(): HTMLCanvasElement {
    if (this.sheenSprite) return this.sheenSprite;
    const c = document.createElement('canvas');
    c.width = 128; c.height = 8;
    const g = c.getContext('2d');
    if (g) {
      const grad = g.createLinearGradient(0, 0, 128, 0);
      grad.addColorStop(0, 'rgba(255,240,200,0)');
      grad.addColorStop(0.45, 'rgba(255,248,225,.55)');
      grad.addColorStop(0.5, 'rgba(255,255,255,.9)');
      grad.addColorStop(0.55, 'rgba(255,248,225,.55)');
      grad.addColorStop(1, 'rgba(255,240,200,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 128, 8);
    }
    this.sheenSprite = c;
    return c;
  }

  private draw(card: HTMLElement): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const r = card.getBoundingClientRect();
    const k = this.scaleOf(r.width);
    const s = this.sim, b = s.beat as ForgeBeat;
    const cw = Math.round(r.width + PAD_X * 2 * k), ch = Math.round(r.height + (PAD_TOP + PAD_BOTTOM) * k);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (cw !== this.cssW || ch !== this.cssH || dpr !== this.dpr) {
      this.cssW = cw; this.cssH = ch; this.dpr = dpr;
      this.canvas.width = Math.max(1, Math.round(cw * dpr));
      this.canvas.height = Math.max(1, Math.round(ch * dpr));
      this.canvas.style.width = cw + 'px';
      this.canvas.style.height = ch + 'px';
    }
    const px = Math.round(r.left - PAD_X * k), py = Math.round(r.top - PAD_TOP * k);
    if (px !== this.posX || py !== this.posY) { this.posX = px; this.posY = py; this.canvas.style.transform = 'translate(' + px + 'px,' + py + 'px)'; }
    if (!this.shown) { this.shown = true; this.canvas.style.display = 'block'; }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // 카드 기준 좌표(배율 k, 여백만큼 옮김)
    ctx.setTransform(dpr * k, 0, 0, dpr * k, dpr * PAD_X * k, dpr * PAD_TOP * k);
    const W = s.w, H = s.h, t = s.t;
    const tr = b.kind === 'transcend';
    const big = b.kind === 'big' || tr;

    // 1) 카드 테 빛
    const rim = s.rimAt();
    if (rim > 0.01) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = rim;
      ctx.strokeStyle = this.rimStr;
      ctx.shadowColor = this.rimStr;
      ctx.shadowBlur = 16;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.roundRect(1, 1, W - 2, H - 2, 10);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // 2) 실패의 가라앉는 한 박자: 카드가 잠깐 어두워진다(섬광의 반대, 0.35초)
    if (b.crack > 0 && t < 0.35) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 0.28 * (1 - t / 0.35);
      ctx.fillStyle = CRACK_CORE;
      ctx.beginPath();
      ctx.roundRect(0, 0, W, H, 10);
      ctx.fill();
    }
    // 3) 균열(실패·하락·보호): 결과 색으로 달궈진 틈(번진 빛) + 밝은 심. 그어지는 길이 reach
    const cr = s.crackAt();
    if (cr.alpha > 0.01 && s.crackN > 1) {
      for (let pass = 0; pass < 2; pass++) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = cr.alpha * (pass === 0 ? 0.7 : 0.95);
        ctx.strokeStyle = pass === 0 ? this.colorStr : CRACK_HOT;
        ctx.shadowColor = this.colorStr;
        ctx.shadowBlur = pass === 0 ? 10 : 0;
        ctx.lineWidth = pass === 0 ? 3.6 : 1.3;
        ctx.lineJoin = 'miter';
        ctx.beginPath();
        let prevB = -1, drawn = 0, total = 0;
        for (let i = 1; i < s.crackN; i++) if (s.cb[i] === s.cb[i - 1]) total++;
        const maxSeg = cr.reach * total / Math.max(1, b.branches);
        for (let i = 0; i < s.crackN; i++) {
          if (s.cb[i] !== prevB) { prevB = s.cb[i]; drawn = 0; ctx.moveTo(s.cx[i], s.cy[i]); continue; }
          if (drawn >= maxSeg) continue;
          const f = Math.min(1, maxSeg - drawn);
          const x0 = s.cx[i - 1], y0 = s.cy[i - 1];
          ctx.lineTo(x0 + (s.cx[i] - x0) * f, y0 + (s.cy[i] - y0) * f);
          drawn++;
        }
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
    }

    // 4) 보호: 균열을 막는 청색 방패 호(타격점 아래 반원)
    if (b.shield && !s.reduced && t > 0.05) {
      const u = Math.min(1, (t - 0.05) / 0.12), fade = 1 - Math.max(0, (t - 0.45) / 0.45);
      if (fade > 0) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.9 * fade;
        ctx.strokeStyle = this.colorStr;
        ctx.shadowColor = this.colorStr;
        ctx.shadowBlur = 12;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(s.ox, s.oy + 6, 18 + 10 * u, Math.PI * 0.12, Math.PI * 0.88);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }

    // 5) 망치 섬광: 타격점 빛 + 모루 줄(카드 폭 가로 섬광 선)
    const fl = s.flashAt();
    if (fl > 0.01) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = fl;
      const R = (big ? 120 + 8 * b.step : 90) * (0.6 + 0.4 * Math.min(1, t / 0.05));
      ctx.drawImage(this.glowOf(this.colorStr), s.ox - R, s.oy - R, R * 2, R * 2);
      ctx.globalAlpha = fl * 0.8;
      ctx.fillStyle = '#fff6dc';
      ctx.fillRect(6, s.oy - 1.5, W - 12, 3);
    }

    // 6) 충격 고리(성공 1, 큰 성공 2 — 두 번째는 0.08초 늦게)
    if (!s.reduced) {
      for (let i = 0; i < b.rings; i++) {
        const u = (t - i * 0.08) / 0.5;
        if (u <= 0 || u >= 1) continue;
        const e = 1 - (1 - u) * (1 - u) * (1 - u);
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = (1 - u) * (big ? 0.95 : 0.6);
        ctx.strokeStyle = this.colorStr;
        ctx.lineWidth = 3.5 * (1 - u) + 0.8;
        ctx.beginPath();
        ctx.ellipse(s.ox, s.oy, 12 + e * W * (big ? 0.62 : 0.42), 6 + e * 46, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // 7) 빛 띠(큰 성공): 카드 안을 왼쪽 아래 → 오른쪽 위로 가로지른다(카드 모양으로 잘라 낸다)
    if (b.sheen && !s.reduced && t > 0.06 && t < 0.62) {
      const u = (t - 0.06) / 0.56;
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(0, 0, W, H, 10);
      ctx.clip();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.85 * Math.sin(Math.PI * u);
      ctx.translate(-0.4 * W + u * 1.8 * W, H * 0.5);
      ctx.rotate(-0.42);
      ctx.drawImage(this.sheen(), -W * 0.25, -H, W * 0.5, H * 2);
      ctx.restore();
      ctx.setTransform(dpr * k, 0, 0, dpr * k, dpr * PAD_X * k, dpr * PAD_TOP * k);
    }

    // 7b) 초월: +17부터 빛기둥(타격점에서 카드 위로 솟는 기둥), +19부터 회전 광선
    if (tr && !s.reduced) {
      const u = Math.min(1, t / b.dur);
      if (b.step >= 2) {
        const env = Math.sin(Math.PI * Math.min(1, u * 1.15));
        const pw = 34 + 10 * b.step;
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.75 * env;
        ctx.drawImage(this.pillar(this.colorStr), s.ox - pw / 2, s.oy - PAD_TOP + 8, pw, PAD_TOP + H * 0.5);
      }
      if (b.step >= 4) {
        const n = 12, rot = t * 0.9, len = Math.min(W * 0.75, 190) * (0.5 + 0.5 * Math.min(1, t / 0.25));
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineCap = 'round';
        ctx.strokeStyle = this.colorStr;
        ctx.lineWidth = 2.2;
        ctx.globalAlpha = 0.55 * Math.sin(Math.PI * u);
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
          const a = rot + (i * Math.PI * 2) / n;
          ctx.moveTo(s.ox + Math.cos(a) * 22, s.oy + Math.sin(a) * 22);
          ctx.lineTo(s.ox + Math.cos(a) * len, s.oy + Math.sin(a) * len);
        }
        ctx.stroke();
      }
    }

    // 8) 입자: 불꽃(속도 방향 짧은 선), 떠오르는 불씨(작은 빛), 떨어지는 불씨(어두운 점)
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    for (let i = 0; i < s.n; i++) {
      const a = s.age[i] / s.life[i];
      const kd = s.kind[i];
      if (kd === PartKind.Spark) {
        ctx.globalAlpha = a < 0.7 ? 1 : 1 - (a - 0.7) / 0.3;
        ctx.strokeStyle = tr ? (a < 0.25 ? '#ffffff' : b.step >= 5 ? hex(PRISM_COLORS[i % PRISM_COLORS.length]) : this.colorStr) : SPARK_COL[a < 0.25 ? 0 : a < 0.6 ? 1 : 2];
        ctx.lineWidth = s.size[i];
        ctx.beginPath();
        ctx.moveTo(s.px[i], s.py[i]);
        ctx.lineTo(s.px[i] - s.vx[i] * 0.028, s.py[i] - s.vy[i] * 0.028);
        ctx.stroke();
      } else if (kd === PartKind.Mote) {
        const flick = 0.75 + 0.25 * Math.sin((s.age[i] + i * 0.37) * 23);
        ctx.globalAlpha = (a < 0.2 ? a / 0.2 : 1 - (a - 0.2) / 0.8) * flick;
        const R = s.size[i] * 3.2;
        ctx.drawImage(this.glowOf(this.colorStr), s.px[i] - R, s.py[i] - R, R * 2, R * 2);
      } else {
        ctx.globalAlpha = (1 - a) * 0.85;
        ctx.fillStyle = b.kind === 'down' ? EMBER_COL : this.colorStr;
        const R = s.size[i];
        ctx.fillRect(s.px[i] - R * 0.5, s.py[i] - R * 0.5, R, R);
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
}
